/*
 * project-dust-field.js — ambient dust across the WHOLE hub page.
 *
 * A single fixed, viewport-sized canvas layered in front of the page
 * content (below the header) with `multiply` blend and ink-coloured
 * motes, so the dust reads over the light paper sections. On the dark
 * hero, multiply leaves it near-invisible — the hero runs its own
 * lighter glow dust (project-hero.js), so the two layers coexist.
 *
 * Skipped entirely under prefers-reduced-motion. Pauses when the tab
 * is hidden. Only runs on /pages/make-* hub pages (where a
 * <project-hero data-hub-page> exists).
 */
(function () {
  if (window.__projectDustField) return;
  window.__projectDustField = true;

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  function start() {
    if (!document.querySelector('project-hero[data-hub-page]')) return;

    let ctx;
    try {
      const probe = document.createElement('canvas');
      if (!probe.getContext || !(ctx = probe.getContext('2d'))) return;
    } catch (e) {
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.className = 'project-dust-field';
    canvas.setAttribute('aria-hidden', 'true');
    document.body.appendChild(canvas);

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const c = canvas.getContext('2d');
    let w = 0;
    let h = 0;
    let particles = [];
    let raf = 0;

    // Soft palette: warm white, dusty pink, beige.
    const palette = ['255, 252, 248', '244, 198, 208', '236, 222, 197'];
    const sprites = palette.map(makeSprite);

    function makeSprite(col) {
      const size = 64;
      const cv = document.createElement('canvas');
      cv.width = size;
      cv.height = size;
      const g = cv.getContext('2d');
      const grad = g.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
      grad.addColorStop(0, 'rgba(' + col + ', 1)');
      grad.addColorStop(0.4, 'rgba(' + col + ', 0.5)');
      grad.addColorStop(1, 'rgba(' + col + ', 0)');
      g.fillStyle = grad;
      g.fillRect(0, 0, size, size);
      return cv;
    }

    function resize() {
      w = window.innerWidth;
      h = window.innerHeight;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      c.setTransform(dpr, 0, 0, dpr, 0, 0);
      seed();
    }

    function seed() {
      const count = Math.max(60, Math.min(240, Math.round((w * h) / 7000)));
      particles = [];
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: 0.6 + Math.random() * 1.8,
          a: 0.16 + Math.random() * 0.34,
          vy: -(0.05 + Math.random() * 0.22),
          vx: (Math.random() - 0.5) * 0.12,
          sway: Math.random() * Math.PI * 2,
          swaySpeed: 0.002 + Math.random() * 0.004,
          twinkle: 0.4 + Math.random() * 0.6,
          tw: Math.random() * Math.PI * 2,
          s: Math.floor(Math.random() * sprites.length),
        });
      }
    }

    function draw() {
      c.clearRect(0, 0, w, h);
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.sway += p.swaySpeed;
        p.tw += 0.02;
        p.x += p.vx + Math.sin(p.sway) * 0.18;
        p.y += p.vy;
        if (p.y < -8) { p.y = h + 8; p.x = Math.random() * w; }
        if (p.x < -8) p.x = w + 8;
        else if (p.x > w + 8) p.x = -8;
        const alpha = Math.max(0, Math.min(1, p.a * (0.6 + 0.4 * Math.sin(p.tw) * p.twinkle)));
        const sz = p.r * 5;
        c.globalAlpha = alpha;
        c.drawImage(sprites[p.s], p.x - sz / 2, p.y - sz / 2, sz, sz);
      }
      c.globalAlpha = 1;
    }

    function play() {
      if (raf) return;
      const loop = () => {
        draw();
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    }

    function pause() {
      if (!raf) return;
      cancelAnimationFrame(raf);
      raf = 0;
    }

    resize();
    play();

    let resizeTimer = 0;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(resize, 200);
    }, { passive: true });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) pause();
      else play();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
